import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowBigUp,
  Camera,
  Clapperboard,
  CloudOff,
  Flame,
  ListPlus,
  Newspaper,
  Plus,
  Radar,
  Send,
  Sparkles,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { keys, useCollections, useFeed, useFeedCounts, useSources } from '../api/queries'
import { isActive, useMyJobs, type ToolJob } from '../api/tools'
import type { FeedCounts, FeedFormat, FeedParams, FeedSort, FeedView, Platform } from '../api/types'
import { useUser } from '../auth/context'
import { Button } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { AddedDialog } from '../components/feed/AddedDialog'
import { FeedSortControl } from '../components/feed/FeedSortControl'
import { FeedTile, TileSkeleton } from '../components/feed/FeedTile'
import { FeedVideoGrid as Grid, FeedTextGrid as TextGrid } from '../components/feed/FeedGrid'
import { TextCard, TextCardSkeleton } from '../components/feed/TextCard'
import { PageHeader } from '../components/PageHeader'
import { Chip, Select } from '../components/Select'
import { Spinner } from '../components/Spinner'
import { toast } from '../components/Toast'
import {
  FEED_PERIODS as PERIODS,
  FORMAT_LABEL,
  FORMAT_PLATFORMS,
  FORMATS,
  TAB_PARAMS,
  defaultSort,
  enterAdded,
  isFormat,
  leaveAdded,
  rememberFormat,
  rememberedFormat,
  restoreTabParams,
  sortLabel,
  sortsFor,
  stashTabParams,
  stashedAutoShared,
} from '../lib/feedFormat'
import { setFeedOrder } from '../lib/feedNav'
import { PLATFORM_LABEL, handleLabel, plural } from '../lib/format'
import { isNewSince, useFeedBaselines } from '../lib/lastVisit'
import { useIsDesktop } from '../lib/useIsDesktop'
import { useViewState } from '../lib/navigation'
import { metricView } from '../lib/measurement'
import { feedRestoring } from '../lib/feedRestore'
import { MetricViewSelect } from '../components/MetricViewSelect'

const TEXT_PLATFORMS: Platform[] = ['telegram', 'reddit', 'instagram']
const PLATFORM_ICON = { instagram: Camera, telegram: Send, reddit: ArrowBigUp } as const
const FORMAT_ICON = { video: Clapperboard, text: Newspaper } as const

const PAGE = 30

const OUTLIERS_HINT =
  'Только посты, набравшие вдвое больше обычного у автора или больше. Репосты из каналов показываются всегда.'

function intParam(value: string | null): number | undefined {
  const n = Number(value)
  return value && Number.isInteger(n) && n > 0 ? n : undefined
}

/** Вкладка и фильтры живут в адресе: возврат из карточки и ссылка коллеге сохраняют их. */
function useFeedFilters() {
  const [params, setParams] = useSearchParams()
  const [defaultFormat] = useViewState('feed.defaultFormat', rememberedFormat)
  const formatRaw = params.get('format')
  const format: FeedFormat = isFormat(formatRaw) ? formatRaw : defaultFormat
  // «Добавленные» — вид вкладки «Видео». Не запоминается: без view в адресе — «Авто».
  const view: FeedView = format === 'video' && params.get('view') === 'added' ? 'added' : 'auto'
  const added = view === 'added'
  const platformRaw = params.get('p')
  // Площадка — фильтр только вкладки «Тексты»: в видео пока одна площадка.
  const platform =
    format === 'text' && TEXT_PLATFORMS.some((p) => p === platformRaw) ? (platformRaw as Platform) : undefined
  const sorts = sortsFor(format, platform, view)
  const sortRaw = params.get('s')
  const sort: FeedSort = sorts.some((s) => s === sortRaw) ? (sortRaw as FeedSort) : defaultSort(view)
  // Общие фильтры «Авто». В «Добавленных» их нет в адресе — они ждут в хранилище,
  // и по ним по-прежнему считаются вкладки «Видео» и «Тексты».
  const shared = useMemo(() => {
    const from = added ? stashedAutoShared() : params
    return {
      collection_id: intParam(from.get('c')),
      days: intParam(from.get('d')) ?? 7,
      only_outliers: from.get('all') !== '1',
    }
  }, [added, params])
  const languageRaw = params.get('lang')
  const language: 'ru' | 'en' | undefined = format === 'video' && (languageRaw === 'ru' || languageRaw === 'en') ? languageRaw : undefined
  const filters = { ...shared, source_id: intParam(params.get('src')), platform, sort, language,
    metric_view: metricView(params) }
  // Период «Добавленных» — по дате добавления, по умолчанию за всё время.
  const addedDays = added ? intParam(params.get('d')) : undefined

  useEffect(() => rememberFormat(format), [format])

  const update = (patch: Record<string, string | number | null | undefined>) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === undefined || v === '') next.delete(k)
          else next.set(k, String(v))
        }
        return next
      },
      { replace: true },
    )
  }

  /** Другая вкладка: её сортировка, площадка и канал возвращаются, общие фильтры остаются. */
  const switchFormat = (next: FeedFormat) => {
    if (next === format) return
    setParams(
      (prev) => {
        // Из «Добавленных» — через «Авто»: «Тексты» получат общие фильтры «Авто».
        const out = added ? leaveAdded(prev) : new URLSearchParams(prev)
        stashTabParams(format, out)
        for (const k of TAB_PARAMS) out.delete(k)
        restoreTabParams(next).forEach((v, k) => out.set(k, v))
        out.set('format', next)
        return out
      },
      { replace: true },
    )
  }

  /** «Авто» ↔ «Добавленные»: у каждого вида свои сортировка, канал и период. */
  const switchView = (next: FeedView) => {
    if (next === view) return
    setParams((prev) => (next === 'added' ? enterAdded(prev) : leaveAdded(prev)), { replace: true })
  }

  return { format, view, sorts, filters, addedDays, update, switchFormat, switchView }
}

/** `add=<ссылки>` (кнопка «В Залёты» в боте) — одноразовый: окно добавления открывается, параметр уходит. */
function useIncomingLinks(): [string | null, (value: string | null) => void] {
  const [params, setParams] = useSearchParams()
  const [links, setLinks] = useViewState<string | null>('feed.adding', () => params.get('add'))
  const raw = params.get('add')
  useEffect(() => {
    if (raw === null) return
    setLinks(raw)
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('add')
        return next
      },
      { replace: true },
    )
  }, [raw, setParams])
  return [links, setLinks]
}

// Задачи «В Залёты», которые лента видела в работе. Вне компонента: уход в «Авто»
// или в другой раздел их не теряет — по возвращении лента всё равно перечитается.
const watching = new Set<string>()

/** Задачи «В Залёты» в работе — полоска «Добавляю N…»; закончилась — лента перечитывается.
 *  Список задач спрашиваем только в «Добавленных». */
function useAddingJobs(enabled: boolean): ToolJob[] {
  const qc = useQueryClient()
  const jobs = useMyJobs({ enabled })
  const active = useMemo(() => (jobs.data ?? []).filter((j) => j.kind === 'feed_add' && isActive(j)), [jobs.data])
  useEffect(() => {
    if (!jobs.data) return
    const now = new Set(active.map((j) => j.id))
    let finished = false
    for (const id of watching) {
      if (now.has(id)) continue
      watching.delete(id)
      finished = true
    }
    now.forEach((id) => watching.add(id))
    if (finished) void qc.invalidateQueries({ queryKey: keys.feedAll })
  }, [jobs.data, active, qc])
  return enabled ? active : []
}

export function FeedPage() {
  const user = useUser()
  const qc = useQueryClient()
  const desktop = useIsDesktop()
  const { format, view, sorts, filters, addedDays, update, switchFormat, switchView } = useFeedFilters()
  const added = view === 'added'
  // В «Добавленных» заход в «Видео» не отмечается: «+N новых» у «Авто» остаются.
  const baselines = useFeedBaselines(user.telegram_id, added ? null : format)
  const baseline = added ? null : baselines[format]
  const collections = useCollections()
  const sources = useSources()
  const [adding, setAdding] = useIncomingLinks()
  const addingJobs = useAddingJobs(added)

  // Счётчики вкладок — по общим фильтрам: подборка, период, «только ×2+».
  // «Добавленные» в них — без фильтров.
  const counts = useFeedCounts({
    metric_view: filters.metric_view,
    language: filters.language,
    collection_id: filters.collection_id,
    days: filters.days,
    only_outliers: filters.only_outliers,
    since_video: baselines.video ?? undefined,
    since_text: baselines.text ?? undefined,
  })
  const addedCounts = counts.data?.added

  const params: FeedParams = added
    ? {
        format: 'video',
        metric_view: filters.metric_view,
        view: 'added',
        source_id: filters.source_id,
        language: filters.language,
        days: addedDays,
        // Сервер его в «Добавленных» не смотрит.
        only_outliers: true,
        sort: filters.sort,
        limit: PAGE,
      }
    : { ...filters, format, limit: PAGE, since: baseline ?? undefined }
  const feed = useFeed(params)
  const selectedMetricView = filters.metric_view ?? feed.data?.pages[0]?.metric_view ?? 'legacy'
  // В памяти навигации только глубина списка. Если query-кэш уже очищен,
  // по возврате последовательно возвращаем страницы из обычного API.
  const [pageCount, setPageCount] = useViewState(`feed.pages.${JSON.stringify(params)}`, 1)
  const loadedPages = feed.data?.pages.length ?? 0
  useEffect(() => {
    if (loadedPages > pageCount) setPageCount(loadedPages)
  }, [loadedPages, pageCount, setPageCount])
  useEffect(() => {
    if (loadedPages > 0 && loadedPages < pageCount && feed.hasNextPage && !feed.isFetching && !feed.isError) {
      void feed.fetchNextPage()
    }
  }, [loadedPages, pageCount, feed.hasNextPage, feed.isFetching, feed.isError, feed.fetchNextPage])
  // После removeQueries observer на один render ещё может показывать прежние
  // страницы. Сигнал готовности берём из актуального cache, а не этого снимка:
  // иначе ScrollRestore завершится до pending/skeleton следующего render.
  const cachedFeed = qc.getQueryState<{ pages: Array<{ next_cursor?: string | null }> }>(keys.feed(params))
  const restoringPages = feedRestoring(cachedFeed, feed, pageCount)
  const items = useMemo(() => feed.data?.pages.flatMap((p) => p.items) ?? [], [feed.data])
  const newCount = added ? null : (feed.data?.pages[0]?.new_count ?? null)
  const [now] = useState(() => Date.now())

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = feed
  useEffect(() => {
    const more = hasNextPage && !isFetchingNextPage ? () => void fetchNextPage() : null
    setFeedOrder(
      items.map((i) => i.id),
      more,
      items,
    )
  }, [items, hasNextPage, isFetchingNextPage, fetchNextPage])

  const tabPlatforms = useMemo(
    () => (filters.platform ? [filters.platform] : FORMAT_PLATFORMS[format]),
    [filters.platform, format],
  )
  const tabSources = useMemo(
    () => (sources.data ?? []).filter((s) => FORMAT_PLATFORMS[format].includes(s.platform)),
    [sources.data, format],
  )
  const tabCollections = useMemo(() => {
    const list = collections.data ?? []
    if (!sources.data) return list.map((c) => ({ collection: c, count: c.source_ids.length }))
    const ids = new Set(tabSources.map((s) => s.id))
    return list
      .map((c) => ({ collection: c, count: c.source_ids.filter((id) => ids.has(id)).length }))
      .filter((x) => x.count > 0 || x.collection.id === filters.collection_id)
  }, [collections.data, sources.data, tabSources, filters.collection_id])

  const channelOptions = useMemo(() => {
    const coll = collections.data?.find((c) => c.id === filters.collection_id)
    return tabSources
      .filter((s) => !coll || coll.source_ids.includes(s.id))
      .filter((s) => tabPlatforms.includes(s.platform))
      .sort((a, b) => a.handle.localeCompare(b.handle))
  }, [tabSources, collections.data, filters.collection_id, tabPlatforms])
  const channel = sources.data?.find((s) => s.id === filters.source_id)
  const noSources = sources.isSuccess && !tabSources.some((s) => tabPlatforms.includes(s.platform))

  // Каналы «Добавленных» — авторы добавленных роликов: их может не быть в источниках.
  const addedAuthors = useMemo(
    () => [...(addedCounts?.authors ?? [])].sort((a, b) => a.handle.localeCompare(b.handle)),
    [addedCounts],
  )
  const addedAuthor = addedAuthors.find((a) => a.source_id === filters.source_id)
  const channelLabel = added
    ? addedAuthor && handleLabel('instagram', addedAuthor.handle)
    : channel && handleLabel(channel.platform, channel.handle)

  const periodDays = added ? addedDays : filters.days
  const periodLabel =
    periodDays === undefined
      ? 'за всё время'
      : (PERIODS.find((p) => p.days === periodDays)?.label.toLowerCase() ?? `за ${periodDays} дн.`)
  const video = format === 'video'
  const defSort = defaultSort(view)

  // Чипы площадок, у которых есть источники: Reddit появится с первым сабреддитом.
  // Площадка без постов за период остаётся с нулём; выбранная видна всегда.
  const textPlatforms = useMemo(() => {
    const all = sources.data
    if (!all) return []
    return TEXT_PLATFORMS.filter((p) => p === filters.platform || all.some((s) => s.platform === p))
  }, [sources.data, filters.platform])

  const pickPlatform = (p: Platform | null) => {
    const keep = sortsFor(format, p ?? undefined).includes(filters.sort)
    update({ p, src: null, s: keep && filters.sort !== 'xn' ? filters.sort : null })
  }

  const periodSelect = added ? (
    <Select label="Период" value={addedDays ?? ''} onChange={(e) => update({ d: e.target.value || null })}>
      <option value="">За всё время</option>
      {PERIODS.map((p) => (
        <option key={p.days} value={p.days}>
          {p.label}
        </option>
      ))}
      {addedDays !== undefined && !PERIODS.some((p) => p.days === addedDays) && (
        <option value={addedDays}>За {addedDays} дн.</option>
      )}
    </Select>
  ) : (
    <Select label="Период" value={filters.days} onChange={(e) => update({ d: Number(e.target.value) === 7 ? null : e.target.value })}>
      {PERIODS.map((p) => (
        <option key={p.days} value={p.days}>
          {p.label}
        </option>
      ))}
      {!PERIODS.some((p) => p.days === filters.days) && <option value={filters.days}>За {filters.days} дн.</option>}
    </Select>
  )

  const openAdd = () => setAdding('')

  return (
    <>
      <PageHeader title="Залёты" description="Посты конкурентов, которые набрали больше обычного у автора." />

      <div className="feed-filters">
        <FormatTabs active={format} counts={counts.data} onChange={switchFormat} />

        {video && (
          <div className="mb-3 flex items-center justify-between gap-2 desk:justify-start desk:gap-3">
            <ViewTabs active={view} addedTotal={addedCounts?.total} onChange={switchView} />
            {added && (
              <Button size="sm" variant="primary" onClick={openAdd}>
                <Plus size={14} />
                <span className="desk:hidden">Добавить</span>
                <span className="hidden desk:inline">Добавить ролик</span>
              </Button>
            )}
          </div>
        )}

        {format === 'text' && textPlatforms.length > 1 && (
          <ChipRow scrollKey="platforms">
            <Chip active={!filters.platform} onClick={() => pickPlatform(null)}>
              Все
              {counts.data && <span className="opacity-60 tabular-nums">{counts.data.text.total}</span>}
            </Chip>
            {textPlatforms.map((p) => {
              const Icon = PLATFORM_ICON[p]
              return (
                <Chip key={p} active={filters.platform === p} onClick={() => pickPlatform(p)}>
                  <Icon size={14} />
                  {PLATFORM_LABEL[p]}
                  {counts.data && <span className="opacity-60 tabular-nums">{counts.data.text.platforms[p] ?? 0}</span>}
                </Chip>
              )
            })}
          </ChipRow>
        )}

        {/* В «Добавленных» подборок нет: туда кладут ролики любых авторов. */}
        {!added && tabCollections.length > 0 && (
          <ChipRow scrollKey="collections">
            <Chip active={!filters.collection_id} onClick={() => update({ c: null, src: null })}>
              Все подборки
            </Chip>
            {tabCollections.map(({ collection: c, count }) => (
              <Chip key={c.id} active={filters.collection_id === c.id} onClick={() => update({ c: c.id, src: null })}>
                {c.name}
                <span className="opacity-60 tabular-nums">{count}</span>
              </Chip>
            ))}
          </ChipRow>
        )}

        <div className="flex flex-col gap-2 desk:flex-row desk:flex-wrap desk:items-center">
          <div data-view-scroll={`feed.${format}.${view}.filters`} className="flex items-center gap-2 overflow-x-auto p-0.5 [scrollbar-width:none] desk:contents">
            {periodSelect}
            <MetricViewSelect value={selectedMetricView} onChange={(value) => update({ metric_view: value })} />
            {video && (
              <Select label="Язык ролика" value={filters.language ?? ''} onChange={(e) => update({ lang: e.target.value || null })}>
                <option value="">Все языки</option>
                <option value="ru">Русский</option>
                <option value="en">English</option>
              </Select>
            )}
            <Select
              label="Канал"
              value={filters.source_id ?? ''}
              onChange={(e) => update({ src: e.target.value || null })}
              className="max-w-44 desk:max-w-56"
            >
              <option value="">Все каналы</option>
              {added ? (
                <>
                  {addedAuthors.map((a) => (
                    <option key={a.source_id} value={a.source_id}>
                      {handleLabel('instagram', a.handle)} · {a.count}
                    </option>
                  ))}
                  {filters.source_id !== undefined && !addedAuthor && <option value={filters.source_id}>Канал</option>}
                </>
              ) : (
                <>
                  {channelOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {handleLabel(s.platform, s.handle)}
                    </option>
                  ))}
                  {channel && !channelOptions.includes(channel) && (
                    <option value={channel.id}>{handleLabel(channel.platform, channel.handle)}</option>
                  )}
                </>
              )}
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 desk:contents">
            {!added && (
              <>
                <Chip
                  active={filters.only_outliers}
                  onClick={() => {
                    update({ all: filters.only_outliers ? '1' : null })
                    // Наведения на телефоне нет: что значит ×2+, говорим при включении.
                    if (!desktop && !filters.only_outliers) toast(OUTLIERS_HINT, { ms: 4000 })
                  }}
                  title={OUTLIERS_HINT}
                >
                  <Flame size={14} />
                  Только залёты ×2+
                </Chip>
                {newCount !== null && newCount > 0 && (
                  <button
                    type="button"
                    onClick={() => update({ s: 'new' })}
                    className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full px-2 text-sm font-medium whitespace-nowrap text-fg hover:underline desk:h-8 desk:text-[13px]"
                    title="Собрано после вашего прошлого захода во вкладку. Показать сначала новые"
                  >
                    <Sparkles size={14} />
                    {newCount} {plural(newCount, 'новый', 'новых', 'новых')}
                    <span className="hidden desk:inline">с прошлого захода</span>
                  </button>
                )}
              </>
            )}
            <FeedSortControl
              desktop={desktop}
              value={filters.sort}
              options={sorts.map((sort) => ({ value: sort, label: sortLabel(sort, format, filters.platform, view) }))}
              onChange={(sort) => update({ s: sort === defSort ? null : sort })}
            />
          </div>
        </div>
      </div>

      {channelLabel && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="text-muted">Канал:</span>
          <span className="font-medium">{channelLabel}</span>
          <Button size="sm" variant="ghost" aria-label="Сбросить канал" onClick={() => update({ src: null })}>
            <X size={14} />
          </Button>
        </div>
      )}

      {added && addingJobs.length > 0 && <AddingStrip jobs={addingJobs} />}

      <div data-view-ready={restoringPages ? 'false' : 'true'}>
      {feed.isPending ? (
        video ? (
          <Grid>
            {Array.from({ length: 12 }, (_, i) => (
              <TileSkeleton key={i} />
            ))}
          </Grid>
        ) : (
          <TextGrid>
            {Array.from({ length: 6 }, (_, i) => (
              <TextCardSkeleton key={i} />
            ))}
          </TextGrid>
        )
      ) : feed.isError ? (
        <EmptyState icon={CloudOff} title="Не удалось загрузить ленту">
          Сервер не ответил. Проверьте соединение.
          <div className="mt-3">
            <Button onClick={() => void feed.refetch()}>Повторить</Button>
          </div>
        </EmptyState>
      ) : items.length === 0 && added ? (
        addedDays !== undefined || filters.source_id !== undefined || filters.language !== undefined ? (
          <EmptyState
            icon={ListPlus}
            title={addedDays !== undefined ? `Добавленных ${periodLabel} нет` : 'С этими фильтрами роликов нет'}
          >
            С этими фильтрами роликов нет.
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {filters.source_id !== undefined && <Button onClick={() => update({ src: null })}>Все каналы</Button>}
              {addedDays !== undefined && <Button onClick={() => update({ d: null })}>За всё время</Button>}
              {filters.language && <Button onClick={() => update({ lang: null })}>Все языки</Button>}
            </div>
          </EmptyState>
        ) : (
          <EmptyState icon={ListPlus} title="Добавленных роликов пока нет">
            Сюда попадают Reels, которые команда добавила сама: кнопкой «Добавить ролик», галочкой «Сохранить в Залёты»
            в разборе видео или кнопкой «В Залёты» в боте.
            <div className="mt-3">
              <Button variant="primary" onClick={openAdd}>
                <Plus size={14} />
                Добавить ролик
              </Button>
            </div>
          </EmptyState>
        )
      ) : items.length === 0 && noSources ? (
        <EmptyState
          icon={Radar}
          title={filters.platform ? `Источников ${PLATFORM_LABEL[filters.platform]} пока нет` : 'Источников пока нет'}
        >
          Они появятся в разделе «Источники», когда сборщик переедет из старого завода.
        </EmptyState>
      ) : items.length === 0 ? (
        <EmptyState icon={Flame} title={filters.only_outliers ? `Залётов ${periodLabel} нет` : `Постов ${periodLabel} нет`}>
          {filters.only_outliers
            ? 'Ни один пост с этими фильтрами не набрал вдвое больше обычного у автора.'
            : 'С этими фильтрами постов не собрано.'}
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {filters.only_outliers && <Button onClick={() => update({ all: '1' })}>Показать все посты</Button>}
            {filters.days < 30 && <Button onClick={() => update({ d: 30 })}>За 30 дней</Button>}
            {filters.language && <Button onClick={() => update({ lang: null })}>Все языки</Button>}
          </div>
        </EmptyState>
      ) : (
        <>
          {video ? (
            <Grid>
              {items.map((item) => (
                <FeedTile
                  key={item.id}
                  item={item}
                  // Добавленные вручную — не «новое»: они не про сбор с прошлого захода.
                  isNew={!item.manual && isNewSince(item.first_seen_at, baseline)}
                  now={now}
                  added={added}
                />
              ))}
              {isFetchingNextPage && Array.from({ length: 6 }, (_, i) => <TileSkeleton key={`more-${i}`} />)}
            </Grid>
          ) : (
            <TextGrid>
              {items.map((item) => (
                <TextCard key={item.id} item={item} isNew={isNewSince(item.first_seen_at, baseline)} now={now} />
              ))}
              {isFetchingNextPage && Array.from({ length: 4 }, (_, i) => <TextCardSkeleton key={`more-${i}`} />)}
            </TextGrid>
          )}
          <div className="mt-6 flex flex-col items-center gap-2">
            {hasNextPage ? (
              <Button onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage && <Spinner size={14} />}
                Показать ещё
              </Button>
            ) : (
              <p className="text-sm text-muted">
                {added
                  ? `${items.length} ${plural(items.length, 'ролик', 'ролика', 'роликов')} — это все добавленные${addedDays !== undefined ? ` ${periodLabel}` : ''}`
                  : `${items.length} ${plural(items.length, 'пост', 'поста', 'постов')} — это всё ${periodLabel}`}
              </p>
            )}
            {feed.isFetchNextPageError && <p className="text-sm text-danger">Не удалось загрузить ещё. Попробуйте снова.</p>}
          </div>
        </>
      )}

      </div>
      {adding !== null && <AddedDialog initial={adding} onClose={() => setAdding(null)} />}
    </>
  )
}

function FormatTabs({
  active,
  counts,
  onChange,
}: {
  active: FeedFormat
  counts: FeedCounts | undefined
  onChange: (format: FeedFormat) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Формат"
      className="mb-3 grid grid-cols-2 gap-1 rounded-xl bg-surface p-1 ring-1 ring-line ring-inset desk:inline-grid desk:min-w-80"
    >
      {FORMATS.map((f) => {
        const c = counts?.[f]
        const selected = f === active
        const Icon = FORMAT_ICON[f]
        const fresh = c?.new_count ?? 0
        return (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(f)}
            title={
              c
                ? `${c.total} ${plural(c.total, 'пост', 'поста', 'постов')}` +
                  (c.new_count !== null ? `, новых с прошлого захода: ${c.new_count}` : '')
                : undefined
            }
            className={`inline-flex h-11 min-w-0 items-center justify-center gap-2 rounded-lg px-3 text-[15px] font-medium transition-colors desk:h-9 desk:px-4 desk:text-sm ${
              selected ? 'bg-card text-link shadow-sm ring-1 ring-line ring-inset' : 'text-muted hover:text-fg'
            }`}
          >
            <Icon size={16} aria-hidden className="shrink-0" />
            {FORMAT_LABEL[f]}
            {c && <span className="font-normal text-muted tabular-nums">{c.total}</span>}
            {fresh > 0 && (
              <span className="rounded-full bg-accent px-1.5 text-[11px] leading-[18px] font-semibold text-accent-fg tabular-nums">
                +{fresh}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

const VIEWS: { view: FeedView; label: string }[] = [
  { view: 'auto', label: 'Авто' },
  { view: 'added', label: 'Добавленные' },
]

/** «Авто | Добавленные N» под вкладкой «Видео». Ширина по подписи: рядом встаёт «Добавить». */
function ViewTabs({
  active,
  addedTotal,
  onChange,
}: {
  active: FeedView
  addedTotal: number | undefined
  onChange: (view: FeedView) => void
}) {
  return (
    <div role="tablist" aria-label="Какие видео" className="inline-flex gap-0.5 rounded-lg bg-surface p-0.5 ring-1 ring-line ring-inset">
      {VIEWS.map(({ view, label }) => {
        const selected = view === active
        return (
          <button
            key={view}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(view)}
            className={`inline-flex h-11 items-center gap-1.5 rounded-md px-3 text-sm font-medium whitespace-nowrap transition-colors desk:h-7 desk:text-[13px] ${
              selected ? 'bg-card text-link shadow-sm ring-1 ring-line ring-inset' : 'text-muted hover:text-fg'
            }`}
          >
            {label}
            {view === 'added' && addedTotal !== undefined && (
              <span className="font-normal text-muted tabular-nums">{addedTotal}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

function AddingStrip({ jobs }: { jobs: ToolJob[] }) {
  const n = jobs.reduce((sum, j) => sum + (j.codes?.length || 1), 0)
  const step = jobs.length === 1 ? (jobs[0]?.progress?.text ?? null) : null
  return (
    <div
      role="status"
      className="mb-3 flex items-center gap-2.5 rounded-xl bg-surface px-3.5 py-2.5 text-sm ring-1 ring-line ring-inset"
    >
      <Spinner size={16} className="shrink-0 text-muted" />
      <p className="min-w-0 truncate">
        <span className="font-medium">
          Добавляю {n} {plural(n, 'ролик', 'ролика', 'роликов')}…
        </span>
        {step && <span className="text-muted"> · {step}</span>}
      </p>
    </div>
  )
}

function ChipRow({ children, scrollKey }: { children: ReactNode; scrollKey: string }) {
  return (
    <div data-view-scroll={`feed.${scrollKey}`} className="mb-2.5 flex gap-2 overflow-x-auto p-0.5 [scrollbar-width:none] desk:flex-wrap desk:p-0">
      {children}
    </div>
  )
}
